import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Text, TextField, styled } from '@streamelements/frontend-ui';
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { SelectSimple } from '../Select';
import { providerIcons } from '../ProviderIcon';
import { ToggleHideStreamKeyButton } from '../../routes/_authenticated.add-output/-ToggleHideStreamKeyButton';
import { platformPrettyName, providersOrder } from '../../constants/common';
// import { useEnableTiktokMutation } from '../../api/enableTiktok';
import { useConnectCustomOutput } from '../../api/connectCustomOutput';
import {
  connectPlatformSchema,
  runAddChannelAuthFlow,
  // useConnectPlatformAccount,
} from '../../api/connectPlatformAccount';
import { AuthContextProvider, ServiceEnvironment } from '@streamelements/client-sdk-js';

const Form = styled('form', {
  display: 'grid',
  gap: '1rem',
  minHeight: '1rem',
  padding: '1.5rem',
  maxWidth: 610,
  mx: 'auto',
  width: '100%',
});

const connectCustomOutputSchema = z.object({
  provider: z.literal('custom'),
  outputName: z
    .string({
      required_error: 'Output name is required',
    })
    .min(1, { message: 'Output name is required' }),
  server: z
    .string({
      required_error: 'Server is required',
    })
    .min(1, { message: 'Server is required' }),
  streamKey: z
    .string({
      required_error: 'Stream key is required',
    })
    .min(1, { message: 'Stream key is required' }),
});

const connectTiktokSchema = z.object({
  provider: z.literal('tiktok'),
});

const formValidation = z.intersection(
  z.discriminatedUnion('provider', [connectPlatformSchema, connectCustomOutputSchema, connectTiktokSchema]),
  z.object({
    streamKeyVisible: z.boolean().default(false),
  })
);

type FormValues = z.infer<typeof formValidation>;

export function AddOutputForm({
  authContextProvider,
  serviceEnvironment,
  defaultValue,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
  defaultValue?: Partial<FormValues>;
}) {
  const form = useForm<FormValues>({
    defaultValues: defaultValue || {
      provider: 'custom',
      streamKeyVisible: false,
    },
    resolver: async (...args) => {
      const result = await zodResolver(formValidation)(...args);
      return result;
    },
    mode: 'onBlur',
  });

  const connectCustomOutput = useConnectCustomOutput({
    authContextProvider,
    serviceEnvironment,
    videoCompositionId: 'default',
    audioCompositionId: 'default',
  });

  /**
   * Trigger a listener so that useFormContext users can get the errors
   */
  form.formState.errors;

  return (
    <FormProvider {...form}>
      <Form
        onSubmit={form.handleSubmit(async (values) => {
          switch (values.provider) {
            case 'custom':
              return connectCustomOutput.mutate(values, {
                onSuccess: () => {
                  form.reset({
                    outputName: '',
                    server: '',
                    streamKey: '',
                    provider: 'custom',
                    streamKeyVisible: false,
                  });
                },
              });
            default: {
              const destinationMetadata = await runAddChannelAuthFlow({
                authContextProvider,
                serviceEnvironment,
                provider: values.provider,
              });

              window.host.endModalDialog(destinationMetadata, console.log);
            }
          }
        }, console.log)}
      >
        <PlatformSelector />

        <ProviderOptions />

        <Box css={{ display: 'flex', justifyContent: 'flex-end' }}>
          <FormAction />
        </Box>
      </Form>
    </FormProvider>
  );
}

function PlatformSelector() {
  const form = useFormContext<FormValues>();

  return (
    <Controller
      control={form.control}
      name="provider"
      render={({ field: provider }) => {
        return (
          <SelectSimple
            label="Platform"
            css={{ width: '100%' }}
            options={
              new Map(
                providersOrder.map((provider) => {
                  const Icon = providerIcons[provider];
                  return [
                    provider,
                    <Text.Body as="span" css={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      {Icon ? <Icon css={{ backgroundColor: provider === 'custom' ? '$divider' : undefined }} /> : null}
                      {platformPrettyName[provider]}
                    </Text.Body>,
                  ];
                })
              )
            }
            value={provider.value}
            onChange={(next) => {
              provider.onChange({ target: { name: provider.name, value: next } });
            }}
          />
        );
      }}
    />
  );
}

function ProviderOptions() {
  const { watch, control } = useFormContext<FormValues>();

  const selectedProvider = watch('provider');

  switch (selectedProvider) {
    case 'custom':
      break;
    default:
      return null;
  }

  return (
    <>
      <Controller
        control={control}
        name="outputName"
        render={({ field, fieldState: { error } }) => {
          return (
            <TextField.Single
              name={field.name}
              label="Output name"
              value={field.value}
              error={!!error}
              onValueChange={(next) => {
                field.onChange({ target: { name: field.name, value: next } });
              }}
              inputProps={{ onBlur: field.onBlur }}
            >
              {error?.message && <TextField.HelperText>{error?.message}</TextField.HelperText>}
            </TextField.Single>
          );
        }}
      />
      <Controller
        control={control}
        name="server"
        render={({ field, fieldState: { error } }) => {
          return (
            <TextField.Single
              name={field.name}
              label="Server URL"
              value={field.value}
              error={!!error}
              onValueChange={(next) => {
                field.onChange({ target: { name: field.name, value: next } });
              }}
              inputProps={{ onBlur: field.onBlur }}
            >
              {error?.message && <TextField.HelperText>{error?.message}</TextField.HelperText>}
            </TextField.Single>
          );
        }}
      />
      <Controller
        control={control}
        name="streamKeyVisible"
        render={({ field: streamKeyVisible }) => {
          return (
            <Controller
              control={control}
              name="streamKey"
              render={({ field: streamKey, fieldState: { error } }) => {
                return (
                  <TextField.Single
                    label="Stream Key"
                    type={streamKeyVisible.value && !error ? 'text' : 'password'}
                    name={streamKey.name}
                    value={streamKey.value}
                    onValueChange={(next) => {
                      streamKey.onChange({ target: { name: streamKey.name, value: next } });
                    }}
                    inputProps={{ autoComplete: 'off', onBlur: streamKey.onBlur }}
                    error={!!error}
                  >
                    <TextField.Adornment>
                      <ToggleHideStreamKeyButton
                        isVisible={streamKeyVisible.value}
                        setVisible={(next) =>
                          streamKeyVisible.onChange({ target: { name: streamKeyVisible.name, value: next } })
                        }
                      />
                    </TextField.Adornment>

                    {error?.message && <TextField.HelperText>{error?.message}</TextField.HelperText>}
                  </TextField.Single>
                );
              }}
            />
          );
        }}
      />
    </>
  );
}

function FormAction() {
  const form = useFormContext<FormValues>();

  return (
    <Controller
      control={form.control}
      name="provider"
      render={({ field: provider }) => {
        switch (provider.value) {
          case 'custom':
            return (
              <Button type="submit" density="compact">
                Add custom output
              </Button>
            );
          default:
            return (
              <Button type="submit" density="compact">
                Connect
              </Button>
            );
        }
      }}
    />
  );
}
